import React, { useEffect, useRef, useState } from 'react';
import { Box, Grow, List, Popper } from '@mui/material';
import MenuItemRow from '../MenuItemRow';
import { PersonIcon, SingletonIcon, SpouseIcon, TwinIcon } from '../MenuIcons';
import { MENU_TONE, SUBMENU_MODIFIERS } from '../menuTheme';
import { ADD_RELATIVE_KEYS } from '../../keyboardShortcuts';
import { config, GENDER, TWIN_TYPE } from '../../pedigree/config';
import { mintObjectIdHex } from '../../pedigree/saveReconciliation';
import {
  resolveSelf,
  resolveOwnerPid,
  hasParents as hasParentsOf,
  hasGrandparents as hasGrandparentsOf
} from '../../pedigree/relativePredicates';
import { PEDIGREE } from 'src/components/Constants';
import {
  relationship,
  getRelationshipForNewPerson,
  getAllowedRelativeActions,
  RELATIVE_ACTIONS
} from '../../pedigree/utils';
import {
  adoptableChildrenForNewSpouse,
  newSpouseSlotsFor,
  addSiblingsToRecords,
  addChildrenToRecords
} from '../../pedigree/edgeIntegrity';
import {
  GENDER_IDENTITY,
  spouseRelations
} from 'src/components/questionnaire/Constants';
import { usePruneOrderForStructuralEdit } from '../../structuralEditContext';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const getNextId = (dataset) => {
  if (!dataset || dataset.length === 0) return 1;

  const numericIds = dataset.map((p) => {
    if (typeof p.pid === 'number') return p.pid;
    else return 0;
  });

  return Math.max(...numericIds) + 1;
};

export const createPerson = (
  gender,
  user_id,
  dataset = [],
  relationshipValue = ''
) => {
  const pid = getNextId(dataset);
  return {
    user_id,
    pid,
    // Born-stable uid: an ObjectId-shaped hex id becomes the FamilyChanged
    // _id on save, so pins/order/views keyed by this uid never orphan across
    // the save round-trip (see mintObjectIdHex).
    uid: mintObjectIdHex(),
    gender,
    genderIdentity: GENDER_IDENTITY[gender] ?? GENDER_IDENTITY.unknown,
    motherId: null,
    fatherId: null,
    spouseIds: [],
    childrenIds: [],
    siblingIds: [],
    relationship: relationshipValue,
    isDynamicallyAdded: true
  };
};

export default function AddRelativeMenu({
  node,
  user,
  setMemberRecords,
  memberRecords,
  setPedigreeState,
  onCloseMenu,
  isDisabled = false
}) {
  const t = useAutoTranslation();

  // Proxy-aware, and shared with the sidebar panel — see relativePredicates.js
  // for why these stopped being two local copies (TGCLOUD-1644).
  const self = resolveSelf(node, memberRecords);
  const hasParents = hasParentsOf(self);

  const allowedActions = getAllowedRelativeActions(node);
  const pruneOrderForStructuralEdit = usePruneOrderForStructuralEdit();
  const ownerPid = resolveOwnerPid(node);

  const [siblingMenuOpen, setSiblingMenuOpen] = useState(false);
  // State (not a ref) anchor: a ref.current set imperatively doesn't reliably drive Popper
  // positioning (the submenu failed to open); state matches ContextMenu's working level-2 flyout.
  const [siblingAnchorEl, setSiblingAnchorEl] = useState(null);

  // Rapid-repeat latch: the structural `disabled` props read the selected node's
  // `data`, which refreshes one-to-two render cycles after an add commits — a
  // fast double-click fires the handler twice before the button greys out. The
  // ref rejects the re-fire synchronously; the state greys the buttons until the
  // burst settles. (The updaters below are ALSO idempotent, so even a click that
  // slips through cannot duplicate structure.)
  const addBusyRef = useRef(false);
  const [addBusy, setAddBusy] = useState(false);
  // The latch is released on a timer, so the timer id has to be held: a bare
  // `setTimeout` here outlived the component and ran `setAddBusy` against a
  // torn-down tree (TGCLOUD-1710). In jsdom that surfaces as an unhandled
  // "ReferenceError: window is not defined" thrown from React's scheduler — every
  // test passes and the Vitest run still exits non-zero, which reads as a flake.
  const addBusyTimerRef = useRef(null);
  const guarded =
    (fn) =>
    (...args) => {
      if (addBusyRef.current) return;
      addBusyRef.current = true;
      setAddBusy(true);
      try {
        fn(...args);
        // Signal for the Clinician Copilot guided walkthrough (read-only, no
        // payload beyond the type — nothing person-identifying leaves this
        // module). Any add action advances a walkthrough step whose
        // advanceOn is 'relative-added'. No-op when no tour is running.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('va:pedigree', {
              detail: { type: 'relative-added' }
            })
          );
        }
      } finally {
        // Belt-and-braces, NOT a second bug fix. TGCLOUD-1710 speculated that
        // rapid adds stacked overlapping timers; they cannot — the `addBusyRef`
        // check above rejects the re-fire before this line is reached, so
        // `addBusyTimerRef.current` is always already null here (measured). This
        // stays only so the invariant survives a future change to the latch.
        clearTimeout(addBusyTimerRef.current);
        addBusyTimerRef.current = setTimeout(() => {
          addBusyTimerRef.current = null;
          addBusyRef.current = false;
          setAddBusy(false);
        }, 400);
      }
    };

  // Nothing may outlive the component. Matches AddRelativePanel's guard, which is
  // the same latch-on-a-timer shape in the sidebar.
  useEffect(() => () => clearTimeout(addBusyTimerRef.current), []);

  const uiDisabled = isDisabled || addBusy;

  // Which union receives a Son/Daughter when the selected person has more than
  // one spouse (D8): user-selectable, defaulting to the most recent spouse (the
  // historical behaviour). Reset when the selection moves to another person.
  // Same proxy-aware resolution as the predicates above — a proxy's children
  // belong to the person it stands in for, so the partner picker lists THEIR
  // spouses.
  const spouseIdsForPicker = self?.spouseIds || [];
  const [childCoParent, setChildCoParent] = useState(null);
  useEffect(() => {
    setChildCoParent(null);
  }, [ownerPid]);
  const effectiveChildCoParent =
    childCoParent != null && spouseIdsForPicker.includes(childCoParent)
      ? childCoParent
      : spouseIdsForPicker.length
      ? spouseIdsForPicker[spouseIdsForPicker.length - 1]
      : null;

  const spouseLabel = (spouseId) => {
    const spouse = memberRecords.find((r) => r.pid === spouseId);
    return spouse
      ? `${spouse.firstName || ''} ${spouse.lastName || ''}`.trim() ||
          t('UPN {{upn}}', { upn: spouse.upn || spouseId })
      : t('UPN {{upn}}', { upn: spouseId });
  };

  const openSiblingMenu = (event, gender) => {
    setSiblingAnchorEl(event.currentTarget);
    setSiblingMenuOpen({ open: true, gender });
  };

  const closeSiblingMenu = () => {
    setSiblingMenuOpen({ open: false });
  };

  // Child adds on a multi-spouse node pick the partner via a NESTED SUBMENU (the
  // same interaction as the Brother/Sister twin submenu). A Select dropdown cannot
  // live inside the context-menu Popper: MUI portals its menu to document.body, so
  // choosing an option registers as a click-away and closes the whole context menu
  // before the selection lands (TGCLOUD-1481).
  //
  // The sidebar used to keep a Select here. It now has its own component
  // (sections/AddRelativePanel), whose spouse chips are NOT portalled — so if the
  // two surfaces are ever to share one selector, chips are the shape that can.
  const [childMenuOpen, setChildMenuOpen] = useState({ open: false });
  const [childAnchorEl, setChildAnchorEl] = useState(null);
  const openChildMenu = (event, gender) => {
    setChildAnchorEl(event.currentTarget);
    setChildMenuOpen({ open: true, gender });
  };
  const closeChildMenu = () => {
    setChildMenuOpen({ open: false });
  };

  // Submenu hygiene: hovering ANY item that doesn't own a submenu dismisses the
  // open ones — otherwise a twin/partner submenu lingers after the pointer moves
  // on, and a stray click there applies an option the user had navigated away
  // from.
  const closeSubmenus = () => {
    closeSiblingMenu();
    closeChildMenu();
  };

  const hasGrandparents = () => hasGrandparentsOf(self, memberRecords);

  const familyLevelMap = new Map(
    memberRecords.map((m) => [m.virtualY, new Set()])
  );
  for (const member of memberRecords) {
    if (familyLevelMap.get(member.virtualY).has(member)) continue;
    else familyLevelMap.get(member.virtualY).add(member);
  }

  const addParents = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const fatherRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.father
    );
    const motherRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.mother
    );

    // Build the parents INSIDE the updater so their pids come from the LATEST
    // array (no collision with another quick add) and they attach to the real
    // person (ownerPid) even when the selected node is a proxy.
    const withParents = (prev) => {
      // Idempotent: a re-fire (double-click) must not create a second couple and
      // orphan the first by overwriting fatherId/motherId.
      const self = prev.find((p) => p.pid === ownerPid);
      if (!self || self.fatherId || self.motherId) return prev;

      const father = createPerson('male', user._id, prev, fatherRel);
      const mother = createPerson(
        'female',
        user._id,
        [...prev, father],
        motherRel
      );

      father.spouseIds.push(mother.pid);
      mother.spouseIds.push(father.pid);
      father.spouseRelationships = {
        [mother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      mother.spouseRelationships = {
        [father.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      father.childrenIds.push(ownerPid);
      mother.childrenIds.push(ownerPid);

      return prev
        .map((person) =>
          person.pid === ownerPid
            ? { ...person, fatherId: father.pid, motherId: mother.pid }
            : person
        )
        .concat([father, mother]);
    };
    setMemberRecords(withParents);
    // New parents re-top a person who is not connected to the proband (an FHQ
    // "Other relative", a native "Unknown person"), moving them down a row, so
    // their stored rank goes in the SAME update (TGCLOUD-1786,
    // structuralEditContext.js). Running the updater again on the records it was
    // computed from gives the prune the same family: only the fresh uids it mints
    // differ, and new people carry no rank.
    pruneOrderForStructuralEdit(memberRecords, withParents(memberRecords));
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSpouse = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const spouseRel = getRelationshipForNewPerson(
      node,
      user,
      relationship.spouse
    );

    setMemberRecords((prev) => {
      // Resolve the selected person + build the spouse against the LATEST array
      // (fresh pid, fresh spouse/child state) — attaches to the real person
      // (ownerPid) even for a proxy.
      const self = prev.find((person) => person.pid === ownerPid);
      if (!self) return prev;

      // The spouse's slot and placeholder sex follow the node's REPRODUCTIVE sex
      // and, for an unknown-sex node, the slot its children already give it —
      // one answer shared with adoptableChildrenForNewSpouse (TGCLOUD-1786).
      const { spouseGender, spouseSlotKey } = newSpouseSlotsFor(prev, self);
      const spouse = createPerson(spouseGender, user._id, prev, spouseRel);

      // Only existing children whose OTHER parent slot is still empty become
      // children of this new (first) spouse. Children that already have a real
      // other parent must not be re-parented onto the spouse, nor copied into
      // its childrenIds (that would orphan the existing parent's edge — I2).
      const adoptable = new Set(adoptableChildrenForNewSpouse(prev, self));

      const arr = prev.map((person) => {
        // Update the selected node with spouse reference
        if (person.pid === ownerPid) {
          return {
            ...person,
            spouseIds: [...person.spouseIds, spouse.pid],
            spouseRelationships: {
              ...(person.spouseRelationships || {}),
              [spouse.pid]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            }
          };
        }

        // Make the spouse the other parent of the node's genuinely-parentless
        // existing children only.
        if (adoptable.has(person.pid)) {
          return { ...person, [spouseSlotKey]: spouse.pid };
        }

        return person;
      });

      const newSpouse = {
        ...spouse,
        spouseIds: [ownerPid],
        spouseRelationships: {
          [ownerPid]: {
            parentRelationship: spouseRelations.MARRIED,
            isConsanguineous: false
          }
        },
        childrenIds: [...adoptable]
      };

      const nodeIndex = arr.findIndex((member) => member.pid === ownerPid);

      let insertIndex = nodeIndex + 1;
      (self.spouseIds || []).forEach((existingSpouseId) => {
        const spouseIdx = arr.findIndex((m) => m.pid === existingSpouseId);
        if (spouseIdx !== -1 && spouseIdx + 1 > insertIndex) {
          insertIndex = spouseIdx + 1;
        }
      });
      arr.splice(insertIndex, 0, newSpouse);
      return arr;
    });

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSingleChild = (gender, coParentPid = effectiveChildCoParent) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;
    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.son : relationship.daughter
    );
    // All co-parent/sibling/pid resolution happens against the LATEST records in
    // the pure transform — never a stale closure (rapid-add fix).
    setMemberRecords((prev) =>
      addChildrenToRecords(prev, ownerPid, {
        count: 1,
        coParentPid,
        makeChild: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));
    onCloseMenu();
  };

  const addChild = () => addSingleChild('male');

  const addDaughter = () => addSingleChild('female');

  const addGrandparents = () => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const withGrandparents = (prev) => {
      // Resolve the selected person from the LATEST array so a quick "Parents
      // then Grandparents" can't read a stale (parentless) node and duplicate
      // the parents. Fresh pids come from the accumulating array below.
      const self = prev.find((p) => p.pid === ownerPid) || node;
      const selfHasBothParents = !!(self.fatherId && self.motherId);

      // Idempotent: if either existing parent already has parents (the same
      // predicate that disables the button), a re-fire must not create a second
      // set of grandparents and re-point the parents at them.
      if (selfHasBothParents) {
        const fatherRec = prev.find((p) => p.pid === self.fatherId);
        const motherRec = prev.find((p) => p.pid === self.motherId);
        if (
          fatherRec?.fatherId ||
          fatherRec?.motherId ||
          motherRec?.fatherId ||
          motherRec?.motherId
        ) {
          return prev;
        }
      }

      const newPeople = [];
      let updatedData = [...prev];
      let fatherId = self.fatherId ?? null;
      let motherId = self.motherId ?? null;

      //Create parents if they don't exist
      if (!selfHasBothParents) {
        if (!fatherId) {
          const father = createPerson(
            'male',
            user._id,
            [...updatedData, ...newPeople],
            getRelationshipForNewPerson(node, user, relationship.father)
          );
          fatherId = father.pid;
          newPeople.push(father);
        }

        if (!motherId) {
          const mother = createPerson(
            'female',
            user._id,
            [...updatedData, ...newPeople],
            getRelationshipForNewPerson(node, user, relationship.mother)
          );
          motherId = mother.pid;
          newPeople.push(mother);
        }

        if (!self.fatherId && !self.motherId) {
          const father = newPeople.find((p) => p.pid === fatherId);
          const mother = newPeople.find((p) => p.pid === motherId);
          if (father && mother) {
            father.spouseIds.push(motherId);
            mother.spouseIds.push(fatherId);
            father.spouseRelationships = {
              [motherId]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            };
            mother.spouseRelationships = {
              [fatherId]: {
                parentRelationship: spouseRelations.MARRIED,
                isConsanguineous: false
              }
            };
          }
        }

        // Link parents to child
        const father =
          newPeople.find((p) => p.pid === fatherId) ||
          updatedData.find((p) => p.pid === fatherId);
        const mother =
          newPeople.find((p) => p.pid === motherId) ||
          updatedData.find((p) => p.pid === motherId);

        if (father && !father.childrenIds.includes(ownerPid)) {
          father.childrenIds.push(ownerPid);
        }
        if (mother && !mother.childrenIds.includes(ownerPid)) {
          mother.childrenIds.push(ownerPid);
        }

        updatedData = updatedData.map((person) => {
          if (person.pid === ownerPid) {
            return {
              ...person,
              fatherId: fatherId,
              motherId: motherId
            };
          }
          return person;
        });
      }

      // Add  grandparents : father-side
      const paternalGrandfather = createPerson(
        'male',
        user._id,
        [...updatedData, ...newPeople],
        relationship.fathersFather
      );
      newPeople.push(paternalGrandfather);

      const paternalGrandmother = createPerson(
        'female',
        user._id,
        [...updatedData, ...newPeople],
        relationship.fathersMother
      );
      newPeople.push(paternalGrandmother);

      paternalGrandfather.spouseIds.push(paternalGrandmother.pid);
      paternalGrandmother.spouseIds.push(paternalGrandfather.pid);
      paternalGrandfather.spouseRelationships = {
        [paternalGrandmother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      paternalGrandmother.spouseRelationships = {
        [paternalGrandfather.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };

      paternalGrandfather.childrenIds.push(fatherId);
      paternalGrandmother.childrenIds.push(fatherId);

      // Add  grandparents: mother-side
      const maternalGrandfather = createPerson(
        'male',
        user._id,
        [...updatedData, ...newPeople],
        relationship.mothersFather
      );
      newPeople.push(maternalGrandfather);

      const maternalGrandmother = createPerson(
        'female',
        user._id,
        [...updatedData, ...newPeople],
        relationship.mothersMother
      );
      newPeople.push(maternalGrandmother);

      maternalGrandfather.spouseIds.push(maternalGrandmother.pid);
      maternalGrandmother.spouseIds.push(maternalGrandfather.pid);
      maternalGrandfather.spouseRelationships = {
        [maternalGrandmother.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };
      maternalGrandmother.spouseRelationships = {
        [maternalGrandfather.pid]: {
          parentRelationship: spouseRelations.MARRIED,
          isConsanguineous: false
        }
      };

      maternalGrandfather.childrenIds.push(motherId);
      maternalGrandmother.childrenIds.push(motherId);

      newPeople.forEach((person) => {
        if (person.pid === fatherId) {
          person.fatherId = paternalGrandfather.pid;
          person.motherId = paternalGrandmother.pid;
        }
        if (person.pid === motherId) {
          person.fatherId = maternalGrandfather.pid;
          person.motherId = maternalGrandmother.pid;
        }
      });

      updatedData = updatedData.map((person) => {
        if (person.pid === fatherId) {
          return {
            ...person,
            fatherId: paternalGrandfather.pid,
            motherId: paternalGrandmother.pid
          };
        }
        if (person.pid === motherId) {
          return {
            ...person,
            fatherId: maternalGrandfather.pid,
            motherId: maternalGrandmother.pid
          };
        }
        return person;
      });

      return [...updatedData, ...newPeople];
    };
    setMemberRecords(withGrandparents);
    // As for addParents: the new generations can move an unconnected person down
    // one or two rows, so their stored rank goes in the same update.
    pruneOrderForStructuralEdit(memberRecords, withGrandparents(memberRecords));

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addSibling = ({ gender, twinType }) => {
    if (node.kind !== config.NODE_TYPE.NODE) return;

    const relationshipValue = getRelationshipForNewPerson(
      node,
      user,
      gender === 'male' ? relationship.fullBrother : relationship.fullSister
    );
    // Build the sibling INSIDE the updater so the selected person, its parents,
    // the sibling group, and the new pid are all read from the LATEST records —
    // never a stale closure. Fixes rapid successive adds landing at the wrong
    // generation / looking like a spouse (the pid collision + stale-parent bug).
    //
    // `twinType` alone says whether this is a co-multiple (TGCLOUD-1692). `isTwin` and
    // `siblingType` are no longer passed: the first was derived from the second, and
    // the second is derived from the size of the resulting set — which is exactly why
    // passing it produced a set of three labelled `twin`. The group id is minted inside
    // `joinTwinSet` when one is actually needed, rather than on every sibling add.
    setMemberRecords((prev) =>
      addSiblingsToRecords(prev, ownerPid, {
        count: 1,
        twinType,
        makeSibling: () => createPerson(gender, user._id, [], relationshipValue)
      })
    );

    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  const addUnknownPerson = () => {
    // Create inside the updater so the pid comes from the LATEST array (no
    // collision with another quick add).
    setMemberRecords((prev) => [
      ...prev,
      createPerson('unknown', user._id, prev, relationship.otherAffected)
    ]);
    setPedigreeState((prev) => ({
      ...prev,
      save: PEDIGREE.saveStatus.IN_PROGRESS
    }));

    onCloseMenu();
  };

  return (
    <Box>
      <List>
        {/* Grandparents and Parents used to grey out with no explanation, which
            is the complaint the sidebar fixed under TGCLOUD-1640. Once they
            exist the row can never become available again, so it renders as
            inert text carrying the reason rather than as a disabled button —
            same reasoning as RecordedChip, and the same "recorded" wording. */}
        {allowedActions.has(RELATIVE_ACTIONS.grandparents) &&
          (hasGrandparents() ? (
            <MenuItemRow
              variant="static"
              icon={<SpouseIcon />}
              tone={MENU_TONE.relationship}
              label={t('Grandparents')}
              hint={t('recorded')}
            />
          ) : (
            <MenuItemRow
              icon={<SpouseIcon />}
              tone={MENU_TONE.relationship}
              shortcut={ADD_RELATIVE_KEYS.grandparents}
              label={t('Grandparents')}
              accessibleName={t('Add grandparents')}
              onMouseEnter={closeSubmenus}
              onClick={guarded(addGrandparents)}
              disabled={uiDisabled}
            />
          ))}
        {allowedActions.has(RELATIVE_ACTIONS.parents) &&
          (hasParents ? (
            <MenuItemRow
              variant="static"
              icon={<SpouseIcon />}
              tone={MENU_TONE.relationship}
              label={t('Parents')}
              hint={t('recorded')}
            />
          ) : (
            <MenuItemRow
              icon={<SpouseIcon />}
              tone={MENU_TONE.relationship}
              shortcut={ADD_RELATIVE_KEYS.parents}
              label={t('Parents')}
              accessibleName={t('Add parents')}
              onMouseEnter={closeSubmenus}
              onClick={guarded(addParents)}
              disabled={uiDisabled}
            />
          ))}
        {allowedActions.has(RELATIVE_ACTIONS.spouse) && (
          <MenuItemRow
            icon={<SpouseIcon />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.spouse}
            label={t('Spouse')}
            accessibleName={t('Add spouse')}
            onMouseEnter={closeSubmenus}
            onClick={guarded(addSpouse)}
            disabled={uiDisabled}
          />
        )}
        {/* A sibling hangs off the shared parents, so without them there is
            nothing to attach to. These CAN become available, so unlike the two
            above they stay real disabled buttons — and now say why, which the
            sidebar already did and the menu did not. The chevron gives way to
            the reason: the submenu is unreachable while it is disabled. */}
        {allowedActions.has(RELATIVE_ACTIONS.brother) && (
          <MenuItemRow
            icon={<PersonIcon gender="male" />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.brother}
            label={t('Brother')}
            accessibleName={t('Add brother')}
            hasSubmenu={hasParents}
            hint={hasParents ? '' : t('Add parents first')}
            disabled={!hasParents}
            onMouseEnter={(e) => {
              closeChildMenu();
              if (hasParents) openSiblingMenu(e, GENDER.male);
            }}
            onClick={(e) => openSiblingMenu(e, GENDER.male)}
          />
        )}
        {allowedActions.has(RELATIVE_ACTIONS.sister) && (
          <MenuItemRow
            icon={<PersonIcon gender="female" />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.sister}
            label={t('Sister')}
            accessibleName={t('Add sister')}
            hasSubmenu={hasParents}
            hint={hasParents ? '' : t('Add parents first')}
            disabled={!hasParents}
            onMouseEnter={(e) => {
              closeChildMenu();
              if (hasParents) openSiblingMenu(e, GENDER.female);
            }}
            onClick={(e) => openSiblingMenu(e, GENDER.female)}
          />
        )}
        {/* The chevron appears only with MORE THAN ONE spouse — with none or one
            there is nothing to pick, so the add is direct. Confirmed correct
            during the TGCLOUD-1644 audit and matches the sidebar's ChildScope. */}
        {allowedActions.has(RELATIVE_ACTIONS.son) && (
          <MenuItemRow
            icon={<PersonIcon gender="male" />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.son}
            label={t('Son')}
            accessibleName={t('Add son')}
            hasSubmenu={spouseIdsForPicker.length > 1}
            disabled={uiDisabled}
            onMouseEnter={(e) => {
              closeSiblingMenu();
              if (spouseIdsForPicker.length > 1) openChildMenu(e, GENDER.male);
              else closeChildMenu();
            }}
            onClick={
              spouseIdsForPicker.length > 1
                ? (e) => openChildMenu(e, GENDER.male)
                : guarded(addChild)
            }
          />
        )}
        {allowedActions.has(RELATIVE_ACTIONS.daughter) && (
          <MenuItemRow
            icon={<PersonIcon gender="female" />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.daughter}
            label={t('Daughter')}
            accessibleName={t('Add daughter')}
            hasSubmenu={spouseIdsForPicker.length > 1}
            disabled={uiDisabled}
            onMouseEnter={(e) => {
              closeSiblingMenu();
              if (spouseIdsForPicker.length > 1)
                openChildMenu(e, GENDER.female);
              else closeChildMenu();
            }}
            onClick={
              spouseIdsForPicker.length > 1
                ? (e) => openChildMenu(e, GENDER.female)
                : guarded(addDaughter)
            }
          />
        )}
        {allowedActions.has(RELATIVE_ACTIONS.unknown) && (
          <MenuItemRow
            icon={<PersonIcon gender="unknown" />}
            tone={MENU_TONE.relationship}
            shortcut={ADD_RELATIVE_KEYS.unknown}
            label={t('Unknown person')}
            accessibleName={t(
              'Add an unknown person, unconnected to this family'
            )}
            onMouseEnter={closeSubmenus}
            onClick={guarded(addUnknownPerson)}
            disabled={uiDisabled}
          />
        )}
      </List>
      <SiblingMenu
        siblingMenuOpen={siblingMenuOpen}
        siblingAnchorEl={siblingAnchorEl}
        handleSiblingTypeSelect={guarded(addSibling)}
      />
      <ChildPartnerMenu
        childMenuOpen={childMenuOpen}
        childAnchorEl={childAnchorEl}
        partnerIds={spouseIdsForPicker}
        partnerLabel={spouseLabel}
        onSelectPartner={guarded((gender, partnerPid) => {
          closeChildMenu();
          addSingleChild(gender, partnerPid);
        })}
      />
    </Box>
  );
}

// "Son → with which partner?" nested submenu for the context-menu variant. Mirrors
// ContextMenu's own level-2 flyout Popper (state anchor + explicit high z-index + plain Grow,
// NO `transition`/function-child and NO inner ClickAwayListener) — that combination is what
// actually opens a Popper nested inside another Popper here. Earlier the missing z-index left it
// painting BEHIND the zIndex-1200 flyout, and the inner ClickAwayListener swallowed the very
// click that opened it. Closing is handled by the parent (hovering a sibling item dismisses it,
// selecting closes the whole menu, and the outer ContextMenu ClickAwayListener catches a click
// fully outside). zIndex 1300 sits above the flyout's 1200.
const ChildPartnerMenu = ({
  childMenuOpen,
  childAnchorEl,
  partnerIds,
  partnerLabel,
  onSelectPartner
}) => {
  const t = useAutoTranslation();
  return (
    <Popper
      open={!!childMenuOpen.open}
      anchorEl={childAnchorEl}
      placement="right-start"
      sx={{ zIndex: 1300 }}
      modifiers={SUBMENU_MODIFIERS}
    >
      <Grow in={!!childMenuOpen.open} style={{ transformOrigin: 'top bottom' }}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            ml: 0.5
          }}
        >
          <List disablePadding>
            {partnerIds.map((partnerPid) => (
              <MenuItemRow
                key={partnerPid}
                icon={<SpouseIcon />}
                tone={MENU_TONE.relationship}
                label={t('with {{partnerLabel}}', {
                  partnerLabel: partnerLabel(partnerPid)
                })}
                onClick={() =>
                  onSelectPartner(childMenuOpen.gender, partnerPid)
                }
              />
            ))}
          </List>
        </Box>
      </Grow>
    </Popper>
  );
};

const SiblingMenu = ({
  siblingMenuOpen,
  siblingAnchorEl,
  handleSiblingTypeSelect
}) => {
  const t = useAutoTranslation();

  // Plain-language zygosity (TGCLOUD-1644), each row carrying the notation it
  // actually draws — the monozygotic bar joining the descent lines, dizygotic
  // without it, and the question mark in the opening for unknown. The glyphs come
  // from twinGroupPath, the same builder the chart uses, so they cannot drift.
  //
  // "Not a twin" rather than "Single", which beside a name and a date of birth
  // reads as marital status. STORED VALUES ARE UNCHANGED — twinType is still
  // singleton/monozygotic/dizygotic/unknown.
  //
  // `twinType` is the ONLY field these rows carry (TGCLOUD-1692). They used to carry a
  // `siblingType` alongside it, which is derived from the size of the resulting set and
  // was the reason a third co-multiple came out labelled `twin`.
  const options = [
    {
      label: 'Not a twin',
      icon: <SingletonIcon />,
      twinType: TWIN_TYPE.singleton
    },
    {
      label: 'Identical twin',
      icon: <TwinIcon type={TWIN_TYPE.monozygotic} />,
      twinType: TWIN_TYPE.monozygotic
    },
    {
      label: 'Non-identical twin',
      icon: <TwinIcon type={TWIN_TYPE.dizygotic} />,
      twinType: TWIN_TYPE.dizygotic
    },
    {
      label: 'Zygosity unknown',
      icon: <TwinIcon type={TWIN_TYPE.unknown} />,
      twinType: TWIN_TYPE.unknown
    }
  ];

  return (
    <Popper
      open={!!siblingMenuOpen.open}
      anchorEl={siblingAnchorEl}
      placement="right-start"
      sx={{ zIndex: 1300 }}
      modifiers={SUBMENU_MODIFIERS}
    >
      <Grow
        in={!!siblingMenuOpen.open}
        style={{ transformOrigin: 'top bottom' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            ml: 0.5
          }}
        >
          <List disablePadding>
            {options.map((option) => (
              <MenuItemRow
                key={option.twinType}
                icon={option.icon}
                tone={MENU_TONE.relationship}
                label={t(option.label)}
                onClick={() =>
                  handleSiblingTypeSelect({
                    gender: siblingMenuOpen.gender,
                    twinType: option.twinType
                  })
                }
              />
            ))}
          </List>
        </Box>
      </Grow>
    </Popper>
  );
};
